import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import {get, find, round} from "lodash"

import padding from "@irati/ui/src/global/padding"
import id from "@irati/utils/id"
import {injectIntl, FormattedMessage} from "@irati/utils/lang"
import {minWidthMedia} from "@irati/ui/src/global/media"
import GetAverageScoreQuery from "@irati/insights/apollo/query/reporting/getAverageScore"
import {AnalysisContext} from "@irati/insights/context"
import Loader from "@irati/ui/src/Loading/Loader"
import {formatDriverName} from "@irati/insights/utils/string"
import {doIHavePermission} from "@irati/utils/permissions/HasPermissionTo"
import auth from "@irati/auth"
import {overrideAuth} from "@irati/insights/utils/apollo"

import {PillBadge} from "../../ui/PillBadge"
import Gauge from "./Gauge"
import ScoreDescription from "./ScoreDescription"
import {LoadingOverlay} from "../../ui/LoadingOverlay"
import SurveysAverageScore from "../Charts/SurveysAverageScore"

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${padding.small};

  ${minWidthMedia.phoneLandscape`
    align-items: center;
  `}
  ${minWidthMedia.desktop`
    flex-direction: row;
    justify-content: space-between;
  `}
`

const GuageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ScoreCard = ({formatMessage, ...props}) => {
  const idNamespace = id("ScoreCard", props)
  const showCOT = doIHavePermission(["insights.changeOverTime"], auth.apps())
  return (
    <AnalysisContext.Consumer>
      {({responseCount: scheduleResponseCount, tenantId: tenant, schedule, filters, isDemo, demoTenantAccessToken}) => {
        const {driverId} = filters
        const isProgressOverTimeEnabled = schedule.surveyCount > 1
        const driver = find(get(schedule, "drivers.results"), {id: filters.driverId})

        const tooltipText = driver
          ? formatMessage({id: "insights.scorecard.tooltip.driver"}, {driver: formatDriverName(formatMessage, driver)})
          : formatMessage({id: "insights.scorecard.tooltip.general"})

        return (
          <GetAverageScoreQuery
            tenant={tenant}
            schedule={schedule}
            filters={filters}
            withHistoricalData={showCOT}
            useLoader={false}
            {...overrideAuth(demoTenantAccessToken, isDemo)}>
            {({stats: {timesAnswered, answersAverage}, historicalStats: {answersAverage: historicalAverageScore} = {}, loading}) => {
              if (loading && !timesAnswered) return <Loader />
              const averageScore = round(answersAverage, 1)
              const responseCount = driverId ? timesAnswered : scheduleResponseCount
              return (
                <LoadingOverlay loading={loading}>
                  <>
                    <ScoreContainer id={idNamespace}>
                      <GuageWrapper>
                        <Gauge
                          id={idNamespace}
                          averageScore={answersAverage}
                          historicalAverageScore={historicalAverageScore}
                          tooltipText={tooltipText}
                          isDriver={!!filters.driverId}
                          min={0}
                          max={5} />
                        <PillBadge id={idNamespace}>
                          <FormattedMessage id="insights.scoreCard.basedOnResponses" values={{responseCount}} />
                        </PillBadge>
                      </GuageWrapper>
                      <ScoreDescription cycle={schedule} driver={driver} averageScore={averageScore} responseCount={responseCount} />
                    </ScoreContainer>
                    {isProgressOverTimeEnabled && <SurveysAverageScore id={idNamespace} schedule={schedule} filters={filters} />}
                  </>
                </LoadingOverlay>
              )
            }}
          </GetAverageScoreQuery>
        )
      }}
    </AnalysisContext.Consumer>
  )
}

ScoreCard.propTypes = {
  formatMessage: PropTypes.func.isRequired
}

export default injectIntl(ScoreCard)
